import {createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useConversation} from './ConversationContext';
import {useFileValidation} from "../utils/fileValidation";
import {useSnackbar} from "./SnackbarContext";
import {DEFEAULT_COLLECTIONS_SIZE, DocumentDataResponse} from "../models/collections.models";
import {useTranslation} from "react-i18next";
import {collectionService} from "../services/collection.service";

export enum DISPLAY_ITEM_TYPE {
    SOURCE_COLLECTIONS = 'source-collections',
    SOURCE_COLLECTION = 'source-collection',
    SOURCE_DOCUMENTS = 'source-documents',
    LOCAL_FILES = 'local-files',
}

export interface AttachedSourceCollection {
    id: number;
    name: string;
    count: number;
}

export interface AttachedSourceDocGroup {
    sourceId: number;
    sourceName: string;
    docIds: number[];
}

export interface AttachmentPreviewItem {
    type: DISPLAY_ITEM_TYPE;
    name: string;
    count?: number;
    sourceId?: number;
    data?: AttachmentPreviewItem[];
}

interface SelectedFilesContextType {
    attachedFiles: File[];
    attachedSourceDocs: number[];
    attachedSourceDocGroups: AttachedSourceDocGroup[];
    attachedSourceCollections: AttachedSourceCollection[];
    setAttachedFiles: (files: File[]) => void;
    setAttachedSourceDocs: (doc: number[], groups: AttachedSourceDocGroup[]) => void;
    setAttachedSourceCollections: (collections: AttachedSourceCollection[]) => void;
    handleAddSourceCollections: (collections: AttachedSourceCollection[]) => void;
    handleRemoveFile: (index: number) => void;
    handleRemoveSourceDoc: (index: number) => void;
    handleRemoveLocalFiles: () => void;
    handleRemoveSourceDocGroup: (sourceId: number) => void;
    handleRemoveSourceCollection: (collectionId: number) => void;
    handleRemoveSourceCollections: (collectionIds: Array<number>) => void;
    handleRemoveAllSourceCollections: () => void;
    handleRemoveAllFiles: () => void;
    hasFiles: boolean;
    isLoadingDefaultCollections: boolean;
    displayItems: AttachmentPreviewItem[];
}

const SelectedFilesContext = createContext<SelectedFilesContextType | undefined>(undefined);

interface SelectedFilesProviderProps {
    children: ReactNode;
}

export const SelectedFilesProvider = ({children}: SelectedFilesProviderProps) => {
    const {validateFile} = useFileValidation();
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const { state, updateDocumentsFromUser, updateSourcesFromUser, updateCollectionsFromUser } = useConversation();

    const [isLoadingDefaultCollections, setIsLoadingDefaultCollections] = useState<boolean>(false);
    const isFetchingDefaultsRef = useRef(false);

    const [attachedFiles, setAttachedFilesState] = useState<File[]>([]);
    const [attachedSourceDocs, setAttachedSourceDocsState] = useState<number[]>([]);
    const [attachedSourceDocGroups, setAttachedSourceDocGroupsState] = useState<AttachedSourceDocGroup[]>([]);
    const [attachedSourceCollections, setAttachedSourceCollectionsState] = useState<AttachedSourceCollection[]>([]);

    const fetchAttachedDefaultCollections = async (): Promise<void> => {
        try {
            setIsLoadingDefaultCollections(true);

            const data = await collectionService.fetchDefaultCollection({page: 0, size: DEFEAULT_COLLECTIONS_SIZE});
            const defaultCollections = data.content.map((
                {id, name, amountOfDocuments}) => ({id, name, count: amountOfDocuments })
            );

            setAttachedSourceCollectionsState(defaultCollections);

            const defaultCollectionIds = defaultCollections.map(c => c.id);
            await updateCollectionsFromUser(defaultCollectionIds);

            setAttachedSourceDocsState([]);
            setAttachedSourceDocGroupsState([]);
            setAttachedFilesState([]);
        } catch (error) {
            console.error(error);
            showSnackbar(t('chatInput.fetchDefaultCollectionsMsgError'), "error");
        } finally {
            setIsLoadingDefaultCollections(false);
        }
    };

    useEffect(() => {
        setAttachedFilesState([]);
        setAttachedSourceDocsState([]);
        setAttachedSourceDocGroupsState([]);
        setAttachedSourceCollectionsState([]);

        if (state.conversationId === null && !isFetchingDefaultsRef.current) {
            isFetchingDefaultsRef.current = true;
            fetchAttachedDefaultCollections().finally(() => isFetchingDefaultsRef.current = false);
        }
    }, [state.conversationId]);

    // UI
    const setAttachedFiles = (files: File[]) => {
        const validFiles = files.filter(file => {
            const validFile = validateFile(file);
            if (!validFile.isValid) {
                showSnackbar(validFile.errorMessage, 'error');
            }
            return validFile.isValid;
        });

        setAttachedFilesState(validFiles);
        setAttachedSourceDocsState([]);
        setAttachedSourceDocGroupsState([]);
        setAttachedSourceCollectionsState([]);
    };

    // UI
    const setAttachedSourceDocs = (doc: number[], groups: AttachedSourceDocGroup[]) => {
        setAttachedSourceDocsState(doc);
        setAttachedSourceDocGroupsState(groups);
        setAttachedFilesState([]);
        setAttachedSourceCollectionsState([]);
    };

    // UI mode - set attachments to display basis on synchronized data from ConversationContext or BE
    // It does not update data with ConversationContext state and BE DB
    // UI
    const setAttachedSourceCollections = (collections: AttachedSourceCollection[]) => {
        setAttachedSourceCollectionsState(collections);
        setAttachedSourceDocsState([]);
        setAttachedSourceDocGroupsState([]);
        setAttachedFilesState([]);
    };

    // UI + BE + context state
    // It adds provided collections to existing ones. Synchronizes with ConversationContext state / BE.
    // Used when SelectedFilesContext is the only source of change
    const handleAddSourceCollections = (collections: AttachedSourceCollection[]) => {
        const existingIds = new Set<number>(attachedSourceCollections.map(c => c.id));
        const newIds = collections.filter(c => !existingIds.has(c.id));

        if (newIds.length === 0) return;

        setAttachedSourceCollectionsState(prev => [...prev, ...newIds]);

        const mergedGroupIds = [...state.selectedGroupIds, ...newIds.map(c => c.id)];
        void updateCollectionsFromUser(mergedGroupIds);

        setAttachedSourceDocsState([]);
        setAttachedSourceDocGroupsState([]);
        setAttachedFilesState([]);
    };

    // UI
    const handleRemoveFile = (index: number) => {
        setAttachedFilesState(prevFiles => {
            const newFiles = [...prevFiles];
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    // UI + BE + context state
    const handleRemoveSourceDoc = (index: number) => {
        setAttachedSourceDocsState(prevDocs => {
            const removedDocId = prevDocs[index];
            const newDocs = prevDocs.filter((doc, i) => i !== index);

            if (removedDocId) {
                setAttachedSourceDocGroupsState(prevGroups => prevGroups
                    .map(group => ({
                        ...group,
                        docIds: group.docIds.filter(docId => docId !== removedDocId),
                    }))
                    .filter(group => group.docIds.length > 0));
            }

            newDocs.length === 0
                ? updateDocumentsFromUser([], [])
                : updateDocumentsFromUser([...newDocs], []);

            return newDocs;
        });
    };

    // UI
    const clearFileInputs = () => {
        ['file-input', 'file-from-disc'].forEach(inputId => {
            const fileInput = document.getElementById(inputId) as HTMLInputElement | null;
            if (fileInput) {
                fileInput.value = '';
            }
        });
    };

    // UI
    const handleRemoveLocalFiles = () => {
        setAttachedFilesState([]);
        clearFileInputs();
    };

    // UI + BE + context state
    const handleRemoveSourceDocGroup = (sourceId: number) => {
        setAttachedSourceDocGroupsState(prevGroups => {
            const removedGroup = prevGroups.find(group => group.sourceId === sourceId);
            if (!removedGroup) {
                return prevGroups;
            }

            setAttachedSourceDocsState(prevDocs => {
                const removedDocIds = new Set(removedGroup.docIds);
                const remainingDocs = prevDocs.filter(docId => !removedDocIds.has(docId));

                remainingDocs.length === 0
                    ? updateDocumentsFromUser([], [])
                    : updateDocumentsFromUser([...remainingDocs], []);

                return remainingDocs;
            });

            return prevGroups.filter(group => group.sourceId !== sourceId);
        });
    };

    // UI + BE + context state
    const handleRemoveSourceCollection = (collectionId: number) => {
        setAttachedSourceCollectionsState(collections => collections.filter(col => col.id !== collectionId));
        setAttachedSourceDocsState([]);
        setAttachedSourceDocGroupsState([]);
        void updateSourcesFromUser([], []);
    };

    // UI + BE + context state
    const handleRemoveSourceCollections = (collectionIds: Array<number>) => {
        setAttachedSourceCollectionsState(collections => {
            return collections.filter(col => !collectionIds.includes(col.id));
        });
        setAttachedSourceDocsState([]);
        setAttachedSourceDocGroupsState([]);
        void updateSourcesFromUser([], []);
    };

    // UI + BE + context state
    const handleRemoveAllSourceCollections = () => {
        setAttachedSourceCollectionsState([]);
        setAttachedSourceDocsState([]);
        setAttachedSourceDocGroupsState([]);
        void updateSourcesFromUser([], []);
    };

    // UI
    const handleRemoveAllFiles = () => {
        setAttachedFilesState([]);
        setAttachedSourceDocsState([]);
        setAttachedSourceDocGroupsState([]);
        setAttachedSourceCollectionsState([]);
        clearFileInputs();
    };

    const hasFiles = attachedFiles.length > 0 || attachedSourceDocs.length > 0 || attachedSourceCollections.length > 0;

    const createSourceCollectionDisplay = (): AttachmentPreviewItem[] => {
        return [{
            type: DISPLAY_ITEM_TYPE.SOURCE_COLLECTION,
            name: attachedSourceCollections[0].name,
            sourceId: attachedSourceCollections[0].id
        }];
    };

    const createSourceCollectionsDisplay = (): AttachmentPreviewItem[] => {
        const data = attachedSourceCollections.map(collection => ({
            type: DISPLAY_ITEM_TYPE.SOURCE_COLLECTION,
            name: collection.name,
            count: collection.count,
            sourceId: collection.id,
        }));

        return ([{
            type: DISPLAY_ITEM_TYPE.SOURCE_COLLECTIONS,
            name: 'collections',
            data: data,
        }]);
    };

    const createLocalFilesDisplay = (): AttachmentPreviewItem[] => {
        return [{
            type: DISPLAY_ITEM_TYPE.LOCAL_FILES,
            name: 'local-files',
            count: attachedFiles.length,
        }];
    };

    const createDocumentsDisplay = (): AttachmentPreviewItem[] => {
        return attachedSourceDocGroups.map(group => ({
            type: DISPLAY_ITEM_TYPE.SOURCE_DOCUMENTS,
            name: group.sourceName,
            count: group.docIds.length,
            sourceId: group.sourceId,
        }));
    };

    const displayItems: AttachmentPreviewItem[] = useMemo(() => {
        if (attachedFiles.length > 0) return createLocalFilesDisplay();
        if (attachedSourceDocGroups.length > 0) return createDocumentsDisplay();
        if (attachedSourceCollections.length === 1) return createSourceCollectionDisplay();
        if (attachedSourceCollections.length > 1) return createSourceCollectionsDisplay();
        return [];
    }, [attachedFiles, attachedSourceDocGroups, attachedSourceCollections]);

    return (
        <SelectedFilesContext.Provider value={{
            attachedFiles,
            attachedSourceDocs,
            attachedSourceDocGroups,
            attachedSourceCollections,
            setAttachedFiles,
            setAttachedSourceDocs,
            setAttachedSourceCollections,
            handleAddSourceCollections,
            handleRemoveFile,
            handleRemoveSourceDoc,
            handleRemoveLocalFiles,
            handleRemoveSourceDocGroup,
            handleRemoveSourceCollection,
            handleRemoveSourceCollections,
            handleRemoveAllSourceCollections,
            handleRemoveAllFiles,
            hasFiles,
            isLoadingDefaultCollections,
            displayItems,
        }}>
            {children}
        </SelectedFilesContext.Provider>
    );
};

export const useSelectedFiles = () => {
    const context = useContext(SelectedFilesContext);
    if (!context) {
        throw new Error('useSelectedFiles must be used within a SelectedFilesProvider');
    }
    return context;
};
